import { CONNECTOR_END_STYLE, type ConnectorEndStyle } from '../constants';
import { type Attachable } from '../objects/Connector';
import {
  AttachmentPoint,
  type ConnectControlWithNormalDefinition,
} from '../types';

// Gets all objects connected to this object
export const getConnectorAttachmentObjects = (
  object: Attachable
): Attachable[] => {
  const connectedObjects: Attachable[] = [];
  for (const connectorAttachment of object.connectorAttachments) {
    const connector = connectorAttachment.connector;
    const attachments = connector.attachments
      .filter(att => att?.object.id !== object.id)
      .map(att => att?.object)
      .filter(Boolean);

    // Technically this will always be at most one, but it could be an empty array as well
    connectedObjects.push(...attachments);
  }

  return connectedObjects;
};

export const endStyleToString = (
  endStyle: ConnectorEndStyle
): keyof typeof CONNECTOR_END_STYLE | undefined => {
  return Object.keys(CONNECTOR_END_STYLE).find(
    key => CONNECTOR_END_STYLE[key] === endStyle
  ) as keyof typeof CONNECTOR_END_STYLE | undefined;
};

// Standard inbound connect control definition for objects implementing Connectable
// interface. This should generally be used for the inbound definitions, with custom
// attachment points added/overriding this as needed.
export const standardInboundConnectControlDefinition = (
  allowCenter = true
): ConnectControlWithNormalDefinition => {
  return {
    [AttachmentPoint.Left]: {
      x: -0.5,
      y: 0,
      normal: { x: -1, y: 0 },
      oppositeAttachmentPoint: AttachmentPoint.Right,
    },
    [AttachmentPoint.Right]: {
      x: 0.5,
      y: 0,
      normal: { x: 1, y: 0 },
      oppositeAttachmentPoint: AttachmentPoint.Left,
    },
    [AttachmentPoint.Top]: {
      x: 0,
      y: -0.5,
      normal: { x: 0, y: -1 },
      oppositeAttachmentPoint: AttachmentPoint.Bottom,
    },
    [AttachmentPoint.Bottom]: {
      x: 0,
      y: 0.5,
      normal: { x: 0, y: 1 },
      oppositeAttachmentPoint: AttachmentPoint.Top,
    },
    ...(allowCenter
      ? { [AttachmentPoint.Center]: { x: 0, y: 0, normal: { x: 0, y: 0 } } }
      : {}),
  };
};
